import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActivateIcon,
  Block as SuspendIcon,
  Extension as ModuleIcon,
  People as UsersIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockReset as ResetIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import AppHeader from '../shared/components/AppHeader';
import UserForm from '../components/UserForm';
import authService from '../shared/services/authService';
import {
  listTenants,
  listPlans,
  createTenant,
  updateTenantPlan,
  setTenantStatus,
  toggleTenantModule,
  listTenantModules,
  adminListTenantUsers,
  adminCreateTenantUser,
  adminSetTenantUserActive,
  adminUpdateTenantUser,
  Tenant,
  TenantStatus,
  TenantModuleState,
  TenantUser,
  TenantUserRole,
} from '../services/tenantAdmin';

// Fallback used only if GET /admin/tenants/plans fails; normally the dropdown is
// populated dynamically from the backend (see loadPlans).
const FALLBACK_PLAN_OPTIONS = [
  { code: 'free', label: 'Free' },
  { code: 'advanced', label: 'Advanced' },
];

interface SuperAdminTenantsProps {
  // When embedded inside another page (e.g. the Admin Dashboard tab) skip the
  // standalone AppHeader so there's no double header.
  embedded?: boolean;
}

const SuperAdminTenants: React.FC<SuperAdminTenantsProps> = ({ embedded = false }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [planOptions, setPlanOptions] = useState(FALLBACK_PLAN_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newPlan, setNewPlan] = useState('free');
  const [isCreating, setIsCreating] = useState(false);

  // Module-toggle dialog state
  const [modulesOpen, setModulesOpen] = useState(false);
  const [moduleTenant, setModuleTenant] = useState<Tenant | null>(null);
  const [modules, setModules] = useState<TenantModuleState[]>([]);
  const [moduleEnabled, setModuleEnabled] = useState<Record<string, boolean>>({});
  const [modulesLoading, setModulesLoading] = useState(false);

  // Users dialog state
  const [usersOpen, setUsersOpen] = useState(false);
  const [usersTenant, setUsersTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  // New-user form state
  const [nuEmail, setNuEmail] = useState('');
  const [nuUsername, setNuUsername] = useState('');
  const [nuFirstname, setNuFirstname] = useState('');
  const [nuLastname, setNuLastname] = useState('');
  const [nuPassword, setNuPassword] = useState('');
  const [nuRole, setNuRole] = useState<TenantUserRole>('user');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  // Edit-user dialog state
  const [editUser, setEditUser] = useState<TenantUser | null>(null);

  const loadTenants = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await listTenants();
      setTenants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error('Failed to load tenants');
      setTenants([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const data = await listPlans();
      if (Array.isArray(data) && data.length > 0) {
        setPlanOptions(data.map((p) => ({ code: p.code, label: p.name })));
      }
    } catch (error) {
      console.error('Failed to load plans; using fallback options:', error);
    }
  }, []);

  useEffect(() => {
    loadTenants();
    loadPlans();
  }, [loadTenants, loadPlans]);

  // --- Create tenant ---------------------------------------------------------
  const handleCreate = async () => {
    if (!newName.trim() || !newSlug.trim()) {
      toast.error('Name and slug are required');
      return;
    }
    try {
      setIsCreating(true);
      await createTenant({ name: newName.trim(), slug: newSlug.trim(), plan_code: newPlan });
      toast.success('Tenant created successfully');
      setCreateOpen(false);
      setNewName('');
      setNewSlug('');
      setNewPlan('free');
      loadTenants();
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to create tenant';
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  };

  // --- Change plan -----------------------------------------------------------
  const handlePlanChange = async (tenant: Tenant, planCode: string) => {
    if (planCode === tenant.plan_code) return;
    try {
      await updateTenantPlan(tenant.id, planCode);
      toast.success(`Plan updated for ${tenant.name}`);
      loadTenants();
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to update plan';
      toast.error(msg);
    }
  };

  // --- Suspend / activate ----------------------------------------------------
  const handleToggleStatus = async (tenant: Tenant) => {
    const next: TenantStatus = tenant.status === 'active' ? 'suspended' : 'active';
    try {
      await setTenantStatus(tenant.id, next);
      toast.success(next === 'active' ? `${tenant.name} activated` : `${tenant.name} suspended`);
      loadTenants();
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to update status';
      toast.error(msg);
    }
  };

  // --- Module toggles --------------------------------------------------------
  const openModules = async (tenant: Tenant) => {
    setModuleTenant(tenant);
    setModulesOpen(true);
    setModules([]);
    setModuleEnabled({});
    try {
      setModulesLoading(true);
      const data = await listTenantModules(tenant.id);
      const mods = Array.isArray(data) ? data : [];
      setModules(mods);
      setModuleEnabled(
        mods.reduce<Record<string, boolean>>((acc, m) => {
          acc[m.module_id] = m.enabled;
          return acc;
        }, {}),
      );
    } catch (error) {
      console.error('Failed to load modules:', error);
      toast.error('Failed to load module list');
    } finally {
      setModulesLoading(false);
    }
  };

  const handleModuleToggle = async (moduleId: string, enabled: boolean) => {
    if (!moduleTenant) return;
    // Optimistic UI update.
    setModuleEnabled((prev) => ({ ...prev, [moduleId]: enabled }));
    try {
      await toggleTenantModule(moduleTenant.id, moduleId, enabled);
      toast.success(enabled ? 'Module enabled' : 'Module disabled');
    } catch (error: any) {
      // Revert on failure.
      setModuleEnabled((prev) => ({ ...prev, [moduleId]: !enabled }));
      const msg = error.response?.data?.detail || error.message || 'Failed to toggle module';
      toast.error(msg);
    }
  };

  // --- Tenant users ----------------------------------------------------------
  const loadUsers = useCallback(async (tenantId: string) => {
    try {
      setUsersLoading(true);
      const data = await adminListTenantUsers(tenantId);
      setUsers(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to load tenant users:', error);
      toast.error(error.response?.data?.detail || 'Failed to load users');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const openUsers = (tenant: Tenant) => {
    setUsersTenant(tenant);
    setUsersOpen(true);
    setUsers([]);
    // Reset the create form.
    setNuEmail('');
    setNuUsername('');
    setNuFirstname('');
    setNuLastname('');
    setNuPassword('');
    setNuRole('user');
    loadUsers(tenant.id);
  };

  const handleCreateUser = async () => {
    if (!usersTenant) return;
    if (!nuEmail.trim() || !nuUsername.trim() || !nuPassword.trim()) {
      toast.error('Email, username and password are required');
      return;
    }
    try {
      setIsCreatingUser(true);
      await adminCreateTenantUser(usersTenant.id, {
        email: nuEmail.trim(),
        username: nuUsername.trim(),
        firstname: nuFirstname.trim(),
        lastname: nuLastname.trim(),
        password: nuPassword,
        role: nuRole,
      });
      toast.success('User created successfully');
      setNuEmail('');
      setNuUsername('');
      setNuFirstname('');
      setNuLastname('');
      setNuPassword('');
      setNuRole('user');
      loadUsers(usersTenant.id);
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to create user';
      toast.error(msg);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleToggleUserActive = async (user: TenantUser) => {
    if (!usersTenant) return;
    try {
      await adminSetTenantUserActive(usersTenant.id, user.id, !user.is_active);
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      loadUsers(usersTenant.id);
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to update user';
      toast.error(msg);
    }
  };

  const handleChangeUserRole = async (u: TenantUser, role: TenantUserRole) => {
    if (!usersTenant || u.role === role) return;
    // Guard: never leave the tenant without a tenant_admin.
    if (u.role === 'tenant_admin' && role === 'user') {
      const admins = users.filter((x) => x.role === 'tenant_admin').length;
      if (admins <= 1) {
        toast.error('A tenant must keep at least one Tenant Admin.');
        return;
      }
    }
    try {
      await adminUpdateTenantUser(usersTenant.id, u.id, { role });
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, role } : x)),
      );
      toast.success('Role updated');
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.message || 'Failed to update role';
      toast.error(msg);
    }
  };

  const handleDeleteUser = async (u: TenantUser) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    // Guard: never leave the tenant without a tenant_admin.
    if (u.role === 'tenant_admin') {
      const admins = users.filter((x) => x.role === 'tenant_admin').length;
      if (admins <= 1) {
        toast.error('A tenant must keep at least one Tenant Admin.');
        return;
      }
    }
    try {
      await authService.deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success('User deleted');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (u: TenantUser) => {
    const pw = window.prompt(`New password for "${u.username}":`);
    if (!pw) return;
    try {
      await authService.resetUserPassword(u.id, pw);
      toast.success('Password reset');
    } catch (error: any) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to reset password');
    }
  };

  const planLabel = (code: string | null) =>
    planOptions.find((p) => p.code === code)?.label ?? code ?? '—';

  return (
    <Box>
      {!embedded && <AppHeader />}
      <Container maxWidth="lg" sx={{ mt: embedded ? 0 : 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            Tenant Management
          </Typography>
          <Box display="flex" gap={2}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTenants}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Create Tenant
            </Button>
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Monthly Usage</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        No tenants found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tenants.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {tenant.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{tenant.slug}</TableCell>
                      <TableCell>
                        <Chip
                          label={tenant.status === 'active' ? 'Active' : 'Suspended'}
                          color={tenant.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <Select
                            value={
                              planOptions.some((p) => p.code === tenant.plan_code)
                                ? tenant.plan_code ?? ''
                                : ''
                            }
                            displayEmpty
                            onChange={(e) => handlePlanChange(tenant, e.target.value as string)}
                          >
                            {!planOptions.some((p) => p.code === tenant.plan_code) && (
                              <MenuItem value="" disabled>
                                {planLabel(tenant.plan_code)}
                              </MenuItem>
                            )}
                            {planOptions.map((p) => (
                              <MenuItem key={p.code} value={p.code}>
                                {p.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {tenant.monthly_documents_used}/{tenant.monthly_document_limit}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Manage users">
                          <IconButton size="small" color="primary" onClick={() => openUsers(tenant)}>
                            <UsersIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Manage modules">
                          <IconButton size="small" color="primary" onClick={() => openModules(tenant)}>
                            <ModuleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={tenant.status === 'active' ? 'Suspend' : 'Activate'}>
                          <IconButton
                            size="small"
                            color={tenant.status === 'active' ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(tenant)}
                          >
                            {tenant.status === 'active' ? <SuspendIcon /> : <ActivateIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Create Tenant Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Tenant</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Slug"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              fullWidth
              helperText="Unique URL-safe identifier, e.g. acme-corp"
            />
            <FormControl fullWidth>
              <InputLabel id="new-tenant-plan-label">Plan</InputLabel>
              <Select
                labelId="new-tenant-plan-label"
                label="Plan"
                value={newPlan}
                onChange={(e) => setNewPlan(e.target.value as string)}
              >
                {planOptions.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Module Toggle Dialog */}
      <Dialog open={modulesOpen} onClose={() => setModulesOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Modules{moduleTenant ? ` — ${moduleTenant.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Toggle modules on or off for this tenant. Changes apply immediately.
          </Typography>
          {modulesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : modules.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No modules available.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column">
              {modules.map((m) => (
                <FormControlLabel
                  key={m.module_id}
                  control={
                    <Switch
                      checked={moduleEnabled[m.module_id] ?? false}
                      onChange={(e) => handleModuleToggle(m.module_id, e.target.checked)}
                      color="primary"
                    />
                  }
                  label={m.name}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModulesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Users Dialog */}
      <Dialog open={usersOpen} onClose={() => setUsersOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Users{usersTenant ? ` — ${usersTenant.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {usersLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                          No users found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>{u.username}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={u.role === 'tenant_admin' ? 'tenant_admin' : 'user'}
                              onChange={(e) =>
                                handleChangeUserRole(u, e.target.value as TenantUserRole)
                              }
                            >
                              <MenuItem value="user">User</MenuItem>
                              <MenuItem value="tenant_admin">Tenant Admin</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.is_active ? 'Active' : 'Inactive'}
                            color={u.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={u.is_active ? 'Deactivate' : 'Activate'}>
                            <IconButton
                              size="small"
                              color={u.is_active ? 'warning' : 'success'}
                              onClick={() => handleToggleUserActive(u)}
                            >
                              {u.is_active ? <SuspendIcon /> : <ActivateIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => setEditUser(u)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset password">
                            <IconButton size="small" onClick={() => handleResetPassword(u)}>
                              <ResetIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteUser(u)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Create user
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2}>
              <TextField
                label="First name"
                value={nuFirstname}
                onChange={(e) => setNuFirstname(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Last name"
                value={nuLastname}
                onChange={(e) => setNuLastname(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Username"
                value={nuUsername}
                onChange={(e) => setNuUsername(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={nuEmail}
                onChange={(e) => setNuEmail(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Password"
                type="password"
                value={nuPassword}
                onChange={(e) => setNuPassword(e.target.value)}
                fullWidth
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="new-user-role-label">Role</InputLabel>
                <Select
                  labelId="new-user-role-label"
                  label="Role"
                  value={nuRole}
                  onChange={(e) => setNuRole(e.target.value as TenantUserRole)}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="tenant_admin">Tenant Admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateUser}
                disabled={isCreatingUser}
              >
                {isCreatingUser ? 'Creating...' : 'Create user'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersOpen(false)}>Close</Button>
        </DialogActions>

        {editUser && (
          <UserForm
            open={!!editUser}
            user={editUser as any}
            hideAdminToggle
            onClose={(shouldRefresh?: boolean) => {
              setEditUser(null);
              if (shouldRefresh && usersTenant) loadUsers(usersTenant.id);
            }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default SuperAdminTenants;
